import * as React from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Autocomplete, AutocompleteRenderInputParams, Chip } from "@mui/material";
import { FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import Space from "../../../../components/Space";
import PlanModel from "../../../../models/plan.model";
import { handlePromise } from "../../../../utils/handlePromisse";
import { getPlans } from "../../../../services/plan-service";
import { GiftRequest } from "../../../../services/gift.services";

const TAG_OPTIONS = ['Congresso', 'Parceria', 'Influencer', 'Suporte', 'Reembolso', 'Campanha'];

export type GiftFormHandle = {
    submit: () => void;
    isValid: boolean;
}

type Props = {
    onGiftAdded: (gifts: GiftRequest[]) => void
    onValidChange?: (valid: boolean) => void
}

export const GiftForm = forwardRef<GiftFormHandle, Props>(({ onGiftAdded, onValidChange }, ref) => {
    const [email, setEmail] = useState<string>('');
    const [selectedEmails, setSelectedEmails] = useState<string[]>([]);

    const [plan, setPlan] = useState('');
    const [plans, setPlans] = useState<PlanModel[]>([]);
    const [tag, setTag] = useState<string | null>(null);

    const isValid = selectedEmails.length > 0 && !!plan;

    useEffect(() => {
        onValidChange?.(isValid);
    }, [isValid, onValidChange]);

    const fetchPlans = useCallback(async () => {
        const [response, error] = await handlePromise(getPlans())
        if (!!error) {
            console.log('error', error, error.stack)
            return
        }
        setPlans(response.data as PlanModel[])
    }, [selectedEmails]);

    useEffect(() => {
        fetchPlans()
    }, [fetchPlans]);

    const handleAddEmail = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && email && !selectedEmails.includes(email)) {
            event.preventDefault()

            const newEmail = email.split(/,|\s+|\n/)
            if (newEmail.length > 0) {
                const filtered = newEmail.filter((e) => !selectedEmails.includes(e.trim()));
                setSelectedEmails((emails) => [...emails, ...filtered]);
            } else {
                setSelectedEmails((emails) => [...emails, email.trim()]);
            }

            setEmail('');
        }
    }, [selectedEmails, email, setEmail]);

    const handleRemoveEmail = useCallback((emailToRemove: string) => {
        setSelectedEmails((emails) => emails.filter((e) => e !== emailToRemove));
    }, []);

    const handleSend = useCallback(() => {
        const gifts = selectedEmails.map((e) => {
            return {
                email: e,
                plan: parseInt(plan),
                ...(tag ? { tag } : {}),
            } as GiftRequest
        })

        onGiftAdded(gifts);

        setSelectedEmails([]);
        setPlan('');
        setTag(null);
    }, [selectedEmails, plan, tag, onGiftAdded]);

    useImperativeHandle(ref, () => ({
        submit: handleSend,
        isValid,
    }), [handleSend, isValid]);

    const renderTags = useCallback(() => {
        return selectedEmails.map((option, index) => (
            <Chip
                key={index}
                label={option}
                color={'primary'}
                onDelete={() => handleRemoveEmail(option)}
            />
        ));
    }, [handleRemoveEmail, selectedEmails]);

    return (
        <>
            <FormControl sx={{ width: '100%' }}>
                <InputLabel id="plan-select-label">Plano</InputLabel>
                <Select
                    labelId="plan-select-label"
                    id="plan-select"
                    value={plan}
                    onChange={(event) => setPlan(event.target.value)}
                    label="Plano">
                    {
                        plans.map((p) => (
                            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                        ))
                    }
                </Select>
            </FormControl>

            <Space pixels={16} direction={'v'}/>

            <Autocomplete
                freeSolo
                value={tag}
                options={TAG_OPTIONS}
                onChange={(_, value) => setTag(value)}
                onInputChange={(_, value) => setTag(value || null)}
                renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                        {...params}
                        label={'Tag'}
                        variant="outlined"
                        color="primary"
                        helperText="Origem do brinde (opcional)"
                    />
                )}
            />

            <Space pixels={16} direction={'v'}/>

            <Autocomplete
                multiple
                freeSolo
                value={selectedEmails}
                renderTags={renderTags}
                options={[]}
                renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                        {...params}
                        label={'E-mails'}
                        variant="outlined"
                        color="primary"
                        onKeyDown={handleAddEmail}
                        onChange={(e) => setEmail(e.target.value)}
                        InputProps={{ ...params.InputProps }}
                        helperText="Pressione Enter para adicionar cada e-mail"
                    />
                )}
            />
        </>
    )
});

GiftForm.displayName = 'GiftForm';
